import {
  add,
  call,
  constant,
  type FnName,
  HALF,
  isNumEqual,
  MINUS_ONE,
  mul,
  type Node,
  neg,
  num,
  pow,
  TWO,
  variable,
  variablesOf,
} from "@/lib/tool-kit/calculus/ast";

/**
 * Parse a formula such as `x^2 sin(3x) / (1 + e^-x)` into an expression tree.
 * Safe by construction: no `eval`, no `Function`.
 *
 * Precedence, loosest first:
 *
 *     sum     := term (("+" | "-") term)*
 *     term    := unary (("*" | "/") unary | implicit factor)*
 *     unary   := ("+" | "-") unary | power
 *     power   := atom ("^" signed-power)?              right-associative
 *
 * Reading rules (the page states them too, and the "Read as" echo shows the result):
 *
 * - `-x^2` is −(x²); `2^-x` is 2 to the power −x; `a^b^c` is a^(b^c).
 * - Implicit multiplication has the same precedence as `*` and `/`, evaluated left to
 *   right: `2x`, `3(x+1)`, `(x+1)(x-1)`, `x sin x`, `2pi r` all multiply, and `1/2x` is
 *   (1/2)·x. Write `1/(2x)` for the other reading. A bare number cannot follow a factor
 *   (`x2`, `(x+1)2`): that is refused rather than guessed.
 * - Variables are single letters, so `ab` is a·b. `e` is Euler's number and `pi` or `π`
 *   is π; neither can be a variable.
 * - A function takes brackets, `sin(x)`. The brackets may be left off around one simple
 *   argument — a number, a letter or a run of them with powers — so `sin x`, `ln x^2`
 *   (= ln(x²)) and `sin 2x` (= sin(2x)) work, while `sin x (x+1)` is sin(x)·(x+1).
 * - `sin^2(x)` (or `sin^2 x`) is (sin x)²; `sin^-1(x)` is refused, because it is
 *   ambiguous between arcsin and 1/sin — write `asin(x)` or `1/sin(x)`.
 * - `sqrt(u)` and `√u` are u^(1/2); `exp(u)` is e^u; `log` is base 10, `ln` is natural,
 *   and `log(x, b)` is the base-b logarithm (read as ln(x)/ln(b)). `log2(x)` and
 *   `log10(x)` are logarithms to base 2 and 10; `sech`, `csch` and `coth` are the
 *   reciprocals of cosh, sinh and tanh.
 * - Decimal points only (a comma separates the arguments of `log`). No scientific
 *   notation: write `2*10^-3`.
 */

export type ExprErrorCode =
  | "empty"
  | "too-long"
  | "too-deep"
  | "unexpected-character"
  | "unexpected-token"
  | "unexpected-end"
  | "missing-paren"
  | "unknown-function"
  | "inverse-notation"
  | "bar-notation";

export interface ExprError {
  code: ExprErrorCode;
  /** The offending text, when there is one. */
  token: string;
  /** Character offset in the normalised input. */
  position: number;
}

export type ParseResult =
  | {
      ok: true;
      ast: Node;
      /** Variables in order of first appearance. */
      variables: string[];
      /** True when the input contained a decimal point (the printer then prefers decimals). */
      usesDecimals: boolean;
    }
  | { ok: false; error: ExprError };

export const MAX_EXPRESSION_LENGTH = 300;
const MAX_DEPTH = 50;
const MAX_DIGITS = 15;

/** Function words, longest first so `sinh` beats `sin` and `asin` beats `a` + `sin`. */
const WORDS = [
  "arcsin",
  "arccos",
  "arctan",
  "asin",
  "acos",
  "atan",
  "sinh",
  "cosh",
  "tanh",
  "sech",
  "csch",
  "coth",
  "sqrt",
  "log10",
  "log2",
  "sin",
  "cos",
  "tan",
  "sec",
  "csc",
  "cot",
  "exp",
  "abs",
  "log",
  "ln",
] as const;

type Word = (typeof WORDS)[number];

const ALIASES: Partial<Record<Word, FnName | "exp" | "sqrt">> = {
  arcsin: "asin",
  arccos: "acos",
  arctan: "atan",
};

const INVERSE_CAPABLE: ReadonlySet<string> = new Set([
  "sin",
  "cos",
  "tan",
  "sec",
  "csc",
  "cot",
  "sinh",
  "cosh",
  "tanh",
]);

type Token =
  | { type: "num"; text: string; pos: number }
  | { type: "var"; text: string; pos: number }
  | { type: "const"; text: "pi" | "e"; pos: number }
  | { type: "fn"; text: Word; pos: number }
  | { type: "op"; text: "+" | "-" | "*" | "/" | "^"; pos: number }
  | { type: "lparen"; text: "("; pos: number }
  | { type: "rparen"; text: ")"; pos: number }
  | { type: "comma"; text: ","; pos: number }
  | { type: "root"; text: "√"; pos: number };

const NUMBER = /\d+(?:\.\d*)?|\.\d+/y;
const LETTERS = /[A-Za-z]+/y;
const SPACE = /\s/;
const LOG_BASE = /^(?:10|2)(?![\d.])/;
const SUPERSCRIPT_RUN = /[⁰¹²³⁴⁵⁶⁷⁸⁹⁻⁺]+/g;
const SUPERSCRIPT_DIGITS = "⁰¹²³⁴⁵⁶⁷⁸⁹";
const TIMES = /[×·⋅∙]/g;
const DIVIDE = /[÷∕]/g;
const MINUS = /[−–]/g;
const DOUBLE_STAR = /\*\*/g;
const OPEN_BRACKET = /[[{]/g;
const CLOSE_BRACKET = /[\]}]/g;

/** "x²" → "x^(2)", typographic operators → ASCII, [ ] { } → ( ). */
function normalize(input: string): string {
  return input
    .replace(TIMES, "*")
    .replace(DIVIDE, "/")
    .replace(MINUS, "-")
    .replace(DOUBLE_STAR, "^")
    .replace(OPEN_BRACKET, "(")
    .replace(CLOSE_BRACKET, ")")
    .replace(SUPERSCRIPT_RUN, (run) => {
      const mapped = [...run]
        .map((ch) => {
          if (ch === "⁻") {
            return "-";
          }
          if (ch === "⁺") {
            return "+";
          }
          return String(SUPERSCRIPT_DIGITS.indexOf(ch));
        })
        .join("");
      return `^(${mapped})`;
    });
}

function readSticky(pattern: RegExp, text: string, at: number): string | null {
  pattern.lastIndex = at;
  const match = pattern.exec(text);
  return match ? match[0] : null;
}

/** Split a run of letters into function words, `pi`, `e` and single-letter variables. */
function splitLetters(run: string, start: number): Token[] {
  const tokens: Token[] = [];
  let at = 0;
  while (at < run.length) {
    const rest = run.slice(at);
    const word = WORDS.find((w) => rest.startsWith(w));
    if (word) {
      tokens.push({ type: "fn", text: word, pos: start + at });
      at += word.length;
      continue;
    }
    if (rest.startsWith("pi")) {
      tokens.push({ type: "const", text: "pi", pos: start + at });
      at += 2;
      continue;
    }
    const letter = run[at];
    tokens.push(
      letter === "e"
        ? { type: "const", text: "e", pos: start + at }
        : { type: "var", text: letter, pos: start + at }
    );
    at += 1;
  }
  return tokens;
}

/** `log10` and `log2` carry digits that the letter pattern does not match. */
function logBaseSuffix(letters: string, text: string, at: number): string {
  if (!letters.endsWith("log")) {
    return "";
  }
  const rest = text.slice(at + letters.length);
  const match = LOG_BASE.exec(rest);
  return match ? match[0] : "";
}

function tokenize(text: string): Token[] | ExprError {
  const tokens: Token[] = [];
  let at = 0;
  while (at < text.length) {
    const ch = text[at];
    if (SPACE.test(ch)) {
      at += 1;
      continue;
    }
    const number = readSticky(NUMBER, text, at);
    if (number !== null) {
      tokens.push({ type: "num", text: number, pos: at });
      at += number.length;
      continue;
    }
    if (ch === "π") {
      tokens.push({ type: "const", text: "pi", pos: at });
      at += 1;
      continue;
    }
    let letters = readSticky(LETTERS, text, at);
    if (letters !== null) {
      letters += logBaseSuffix(letters, text, at);
      const split = splitLetters(letters, at);
      const endsInWord = split.at(-1)?.type === "fn";
      if (
        letters.length >= 3 &&
        text[at + letters.length] === "(" &&
        !endsInWord
      ) {
        return { code: "unknown-function", token: letters, position: at };
      }
      tokens.push(...split);
      at += letters.length;
      continue;
    }
    const single = singleCharToken(ch, at);
    if (single === null) {
      return {
        code: ch === "|" ? "bar-notation" : "unexpected-character",
        token: ch,
        position: at,
      };
    }
    tokens.push(single);
    at += 1;
  }
  return tokens;
}

function singleCharToken(ch: string, pos: number): Token | null {
  switch (ch) {
    case "(":
      return { type: "lparen", text: "(", pos };
    case ")":
      return { type: "rparen", text: ")", pos };
    case ",":
      return { type: "comma", text: ",", pos };
    case "√":
      return { type: "root", text: "√", pos };
    case "+":
    case "-":
    case "*":
    case "/":
    case "^":
      return { type: "op", text: ch, pos };
    default:
      return null;
  }
}

/** "2.75" → 11/4 exactly; a literal too long to be exact becomes a float. */
function numberNode(text: string): Node {
  const [whole, fraction = ""] = text.split(".");
  const digits = `${whole || "0"}${fraction}`;
  if (digits.length <= MAX_DIGITS) {
    return num(Number(digits), 10 ** fraction.length);
  }
  return num(Number(text));
}

class ParseFailure extends Error {
  readonly detail: ExprError;
  constructor(detail: ExprError) {
    super(detail.code);
    this.detail = detail;
  }
}

class Parser {
  private index = 0;
  private depth = 0;
  private readonly tokens: Token[];
  private readonly length: number;
  usesDecimals = false;

  constructor(tokens: Token[], length: number) {
    this.tokens = tokens;
    this.length = length;
  }

  parseAll(): Node {
    const node = this.sum();
    const extra = this.peek();
    if (extra) {
      this.fail(
        extra.type === "rparen" ? "missing-paren" : "unexpected-token",
        extra
      );
    }
    return node;
  }

  private peek(): Token | undefined {
    return this.tokens[this.index];
  }

  private next(): Token | undefined {
    const token = this.tokens[this.index];
    this.index += 1;
    return token;
  }

  private fail(code: ExprErrorCode, token?: Token): never {
    throw new ParseFailure({
      code,
      token: token?.text ?? "",
      position: token?.pos ?? this.length,
    });
  }

  private isOp(token: Token | undefined, ...ops: string[]): boolean {
    return token?.type === "op" && ops.includes(token.text);
  }

  private enter() {
    this.depth += 1;
    if (this.depth > MAX_DEPTH) {
      this.fail("too-deep");
    }
  }

  private leave() {
    this.depth -= 1;
  }

  private sum(): Node {
    this.enter();
    let node = this.term();
    while (this.isOp(this.peek(), "+", "-")) {
      const op = this.next()?.text;
      const right = this.term();
      node = add(node, op === "-" ? neg(right) : right);
    }
    this.leave();
    return node;
  }

  /** A letter, constant, function, bracket or root straight after a factor multiplies it. */
  private startsImplicit(token: Token | undefined): boolean {
    return (
      token?.type === "var" ||
      token?.type === "const" ||
      token?.type === "fn" ||
      token?.type === "lparen" ||
      token?.type === "root"
    );
  }

  private term(): Node {
    let node = this.unary();
    let token = this.peek();
    while (this.isOp(token, "*", "/") || this.startsImplicit(token)) {
      if (token?.type === "op") {
        this.next();
        const right = this.unary();
        node =
          token.text === "*"
            ? mul(node, right)
            : mul(node, pow(right, MINUS_ONE));
      } else {
        node = mul(node, this.power());
      }
      token = this.peek();
    }
    if (token?.type === "num") {
      this.fail("unexpected-token", token);
    }
    return node;
  }

  private unary(): Node {
    const token = this.peek();
    if (this.isOp(token, "+")) {
      this.next();
      return this.unary();
    }
    if (this.isOp(token, "-")) {
      this.next();
      this.enter();
      const operand = this.unary();
      this.leave();
      return negated(operand);
    }
    return this.power();
  }

  private power(): Node {
    const base = this.atom();
    if (this.isOp(this.peek(), "^")) {
      this.next();
      return pow(base, this.exponentOperand());
    }
    return base;
  }

  /** What follows "^": an optional sign, then a power (so a^b^c is a^(b^c)). */
  private exponentOperand(): Node {
    this.enter();
    const token = this.peek();
    let node: Node;
    if (this.isOp(token, "-")) {
      this.next();
      node = negated(this.exponentOperand());
    } else if (this.isOp(token, "+")) {
      this.next();
      node = this.exponentOperand();
    } else {
      node = this.power();
    }
    this.leave();
    return node;
  }

  private bracketed(): Node {
    const inner = this.sum();
    const closing = this.peek();
    if (closing?.type !== "rparen") {
      this.fail("missing-paren", closing);
    }
    this.next();
    return inner;
  }

  private atom(): Node {
    const token = this.next();
    if (!token) {
      return this.fail("unexpected-end");
    }
    switch (token.type) {
      case "num":
        if (token.text.includes(".")) {
          this.usesDecimals = true;
        }
        return numberNode(token.text);
      case "var":
        return variable(token.text);
      case "const":
        return constant(token.text);
      case "lparen":
        return this.bracketed();
      case "root": {
        this.enter();
        const operand = this.atom();
        this.leave();
        return pow(operand, HALF);
      }
      case "fn":
        return this.functionCall(token);
      default:
        return this.fail("unexpected-token", token);
    }
  }

  private functionCall(token: Extract<Token, { type: "fn" }>): Node {
    let exponent: Node | null = null;
    if (this.isOp(this.peek(), "^")) {
      this.next();
      exponent = this.exponentOperand();
      if (
        isNumEqual(exponent, -1) &&
        INVERSE_CAPABLE.has(ALIASES[token.text] ?? token.text)
      ) {
        this.fail("inverse-notation", token);
      }
    }
    const { arg, base } = this.argument(token);
    const node = buildCall(token.text, arg, base);
    return exponent ? pow(node, exponent) : node;
  }

  private argument(token: Extract<Token, { type: "fn" }>): {
    arg: Node;
    base: Node | null;
  } {
    const following = this.peek();
    if (following?.type === "lparen") {
      this.next();
      this.enter();
      const arg = this.sum();
      let base: Node | null = null;
      if (this.peek()?.type === "comma" && token.text === "log") {
        this.next();
        base = this.sum();
      }
      this.leave();
      const closing = this.peek();
      if (closing?.type !== "rparen") {
        this.fail(
          closing?.type === "comma" ? "unexpected-token" : "missing-paren",
          closing
        );
      }
      this.next();
      return { arg, base };
    }
    return { arg: this.bareArgument(token), base: null };
  }

  /** `sin x`, `ln x^2`, `sin 2x`, `sin pi x`, `cos sin x` — one simple argument, no brackets. */
  private bareArgument(token: Token): Node {
    const first = this.peek();
    if (!first) {
      return this.fail("unexpected-end");
    }
    if (first.type === "fn") {
      this.next();
      return this.functionCall(first);
    }
    let negative = false;
    if (this.isOp(first, "-", "+")) {
      this.next();
      negative = first.text === "-";
    }
    const lead = this.peek();
    if (
      lead?.type !== "num" &&
      lead?.type !== "var" &&
      lead?.type !== "const"
    ) {
      return this.fail(
        lead ? "unexpected-token" : "unexpected-end",
        lead ?? token
      );
    }
    let node = this.bareFactor();
    while (this.peek()?.type === "var" || this.peek()?.type === "const") {
      node = mul(node, this.bareFactor());
    }
    return negative ? neg(node) : node;
  }

  private bareFactor(): Node {
    const token = this.next();
    let base: Node;
    if (token?.type === "num") {
      if (token.text.includes(".")) {
        this.usesDecimals = true;
      }
      base = numberNode(token.text);
    } else if (token?.type === "var") {
      base = variable(token.text);
    } else if (token?.type === "const") {
      base = constant(token.text);
    } else {
      return this.fail("unexpected-token", token);
    }
    if (this.isOp(this.peek(), "^")) {
      this.next();
      return pow(base, this.exponentOperand());
    }
    return base;
  }
}

/** −n for a literal number n, otherwise (−1)·node. */
function negated(node: Node): Node {
  return node.kind === "num" ? num(-node.n, node.d) : neg(node);
}

function buildCall(word: Word, arg: Node, base: Node | null): Node {
  switch (word) {
    case "arcsin":
      return call("asin", arg);
    case "arccos":
      return call("acos", arg);
    case "arctan":
      return call("atan", arg);
    case "exp":
      return pow(constant("e"), arg);
    case "sqrt":
      return pow(arg, HALF);
    case "log10":
      return call("log", arg);
    case "log2":
      return mul(call("ln", arg), pow(call("ln", TWO), MINUS_ONE));
    case "log":
      return base
        ? mul(call("ln", arg), pow(call("ln", base), MINUS_ONE))
        : call("log", arg);
    case "sech":
      return pow(call("cosh", arg), MINUS_ONE);
    case "csch":
      return pow(call("sinh", arg), MINUS_ONE);
    case "coth":
      return pow(call("tanh", arg), MINUS_ONE);
    default:
      return call(word, arg);
  }
}

export function parseExpression(input: string): ParseResult {
  const trimmed = input.trim();
  if (trimmed === "") {
    return { ok: false, error: { code: "empty", token: "", position: 0 } };
  }
  if (trimmed.length > MAX_EXPRESSION_LENGTH) {
    return { ok: false, error: { code: "too-long", token: "", position: 0 } };
  }
  const normalized = normalize(trimmed);
  const tokens = tokenize(normalized);
  if (!Array.isArray(tokens)) {
    return { ok: false, error: tokens };
  }
  const parser = new Parser(tokens, normalized.length);
  try {
    const ast = parser.parseAll();
    return {
      ok: true,
      ast,
      variables: variablesOf(ast),
      usesDecimals: parser.usesDecimals,
    };
  } catch (error) {
    if (error instanceof ParseFailure) {
      return { ok: false, error: error.detail };
    }
    throw error;
  }
}
